import {withAuthenticationRequired} from "@auth0/auth0-react";
import React, {useCallback} from "react";
import {DataGrid, GridSortModel} from "@mui/x-data-grid";
import {
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle, Grid,
    MenuItem,
    Select,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {useReservationAdminAPI} from "../../service/useReservationAdminAPI";
import Loading from "../common/Loading";
import AppSnackbar from "../../common/AppSnackbar";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import AlertDialogSlide from "../common/AlertDialogSlide";
import {useMutation} from "@apollo/client";
import {CANCEL_BOOKING} from "../../gql/reservationmanagement.gql";
import {MobileCardsView} from "./newReservationComponents/MobileCardsViewComponent";
import {makeReservationColumns} from "./newReservationComponents/NewReservationGridColumnDefinition";
import {useTenantConfig} from "../../security/tenant-config-context";

export type Reservation = any; // Use your real type here if available

const NewReservationsOverview: React.FC = () => {
    const {t} = useTranslation();
    const [cancelBooking] = useMutation(CANCEL_BOOKING);
    const {getNewReservations, validateReservation, downloadContracts, updateReservation, markAdvanceReceived} =
        useReservationAdminAPI();

    const [reservations, setReservations] = React.useState([]);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = React.useState(true);
    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState("");

    // Edit modal
    const [openEditModal, setOpenEditModal] = React.useState(false);
    const [currentUuid, setCurrentUuid] = React.useState(undefined);

    // Alerts
    const [openAlert, setOpenAlert] = React.useState(false);
    const [alertTitle, setAlertTitle] = React.useState("");
    const [alertMessage, setAlertMessage] = React.useState("");
    const [alertAction, setAlertAction] = React.useState("");

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [view, setView] = React.useState(isMobile ? "cards" : "table");

    const { cfg } = useTenantConfig();
    const isHotelFlow = cfg?.businessTypes?.includes('HOTEL_BASED');
    const isAppointmentFlow = cfg?.businessTypes?.includes('APPOINTMENT_BASED');

    // Shared sort state (cards + sync with table)
    const [sortField, setSortField] = React.useState("id");
    const [sortDir, setSortDir] = React.useState("asc");

    React.useEffect(() => {
        setView(isMobile ? "cards" : "table");
    }, [isMobile]);

    function handleFetchError(reason: string) {
        setSnackbarMessage(reason);
        setOpenSnackbar(true);
    }

    function fetch() {
        getNewReservations(isHotelFlow)
            .then((value: Reservation[]) => setReservations(value))
            .catch(() => handleFetchError(t("new_reservations_error")))
            .finally(() => setIsLoading(false));
    }

    React.useEffect(() => {
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpenEditModal = useCallback((uuid: string) => {
        setCurrentUuid(uuid);
        setOpenEditModal(true);
    });
    const handleCloseEditModal = () => setOpenEditModal(false);

    function resetAlertConfig() {
        setOpenAlert(false);
        setAlertTitle("");
        setAlertMessage("");
        setAlertAction("");
    }

    const handleCloseAlert = (result: boolean, action: string) => {
        if (result && action === "VALIDATE_RESERVATION") {
            validateReservation(currentUuid)
                .then(() => fetch())
                .catch(() => handleFetchError(t("validate_reservation_error")));
        } else if (result && action === "CANCEL_RESERVATION") {
            cancelBooking({variables: {bookingUuid: currentUuid}})
                .then(() => fetch())
                .catch(() => handleFetchError(t("cancel_reservation_error")));
        }
        resetAlertConfig();
    };

    const requestValidate = useCallback((uuid: string) => {
        setOpenAlert(true);
        setAlertTitle(t("validateReservationTitle"));
        setAlertMessage(t("validateReservationMessage"));
        setAlertAction("VALIDATE_RESERVATION");
        setCurrentUuid(uuid);
    });

    const requestCancel = useCallback((uuid: string) => {
        setOpenAlert(true);
        setAlertTitle(t("cancelReservationTitle"));
        setAlertMessage(t("cancelReservationMessage"));
        setAlertAction("CANCEL_RESERVATION");
        setCurrentUuid(uuid);
    });

    const navigateWithUuidTo = useCallback((entity: any, path: string) => {
        navigate(`/beheer/${path}/${entity.uuid}`);
    });

    const handleUpdateHours = () => {
        const r = reservations.find((v) => v.uuid === currentUuid);
        if (!r) return;
        updateReservation(r).then(() => {
            handleCloseEditModal();
            fetch();
        });
    };

    const handleSelectChange = (event: any, field: string) => {
        const updatedReservations = reservations.map((reservation) => {
            if (reservation.uuid === currentUuid) {
                return {...reservation, [field]: event.target.value};
            }
            return reservation;
        });
        setReservations(updatedReservations);
    };

    const handleMarkAdvanceReceived = useCallback((uuid) => {
        return markAdvanceReceived(uuid)
            .then(() => fetch())
            .catch(() => handleFetchError(t("mark_advance_received_error")));
    }, [markAdvanceReceived, t]);

    function getRowId(row: any) {
        return row.uuid;
    }

    // Keep DataGrid sort in sync with mobile sort state
    const gridSortModel: GridSortModel = [
        {
            field: sortField,
            sort: sortDir,
        },
    ];

    const handleGridSortModelChange = (model) => {
        if (!model?.length) return;
        const m = model[0];
        if (m.field === sortField && m.sort === sortDir) return;
        // Only map known fields
        if (["id", "checkIn", "checkOut", "price", "advance"].includes(m.field)) {
            setSortField(m.field);
            setSortDir((m.sort) ?? "asc");
        }
    };

    const pageSize = isMobile ? 5 : 20;

    // Edit dialog responsive (fullScreen on mobile)
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const columns = React.useMemo(
        () =>
            makeReservationColumns({
                t,
                isMobile,
                isHotelFlow,
                navigateWithUuidTo,
                handleOpenEditModal,
                downloadContracts,
                requestValidate,
                requestCancel,
                markAdvanceReceived: handleMarkAdvanceReceived,
            }),
        [t, isMobile, isHotelFlow, navigateWithUuidTo, handleOpenEditModal, downloadContracts, requestValidate, requestCancel, handleMarkAdvanceReceived]
    );

    return (
        <>
            {isLoading && <Loading/>}
            {!isLoading && (
                <Grid container justifyContent="center" sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}>
                    <Grid item xs={12} sm={12} sx={{p: 2, m: 2}}>
                    <Card variant="outlined" sx={{width: "100%", mb: 1}}>
                        {view === "cards" ? (
                            <MobileCardsView
                                reservations={reservations}
                                t={t}
                                isHotelFlow={isHotelFlow}
                                navigateWithUuidTo={navigateWithUuidTo}
                                handleOpenEditModal={handleOpenEditModal}
                                downloadContracts={downloadContracts}
                                requestValidate={requestValidate}
                                requestCancel={requestCancel}
                                markAdvanceReceived={handleMarkAdvanceReceived}
                                sortField={sortField}
                                setSortField={setSortField}
                                sortDir={sortDir}
                                setSortDir={setSortDir}
                            />
                        ) : (
                            <Box sx={{p: 1}}>
                                <DataGrid
                                    rows={reservations}
                                    getRowId={getRowId}
                                    columns={columns}
                                    autoHeight
                                    density="compact"
                                    disableColumnMenu
                                    disableColumnSelector
                                    disableDensitySelector
                                    disableRowSelectionOnClick
                                    sortingOrder={["asc", "desc"]}
                                    sortModel={gridSortModel}
                                    onSortModelChange={handleGridSortModelChange}
                                    initialState={{
                                        pagination: {
                                            paginationModel: {page: 0, pageSize},
                                        },
                                    }}
                                    pageSizeOptions={[5, 10, 20]}
                                    localeText={{
                                        noRowsLabel: isAppointmentFlow ? t('noAppointmentsFound') : t('noReservationsFound'),
                                    }}
                                    data-testid="new-reservations-datagrid"
                                />
                            </Box>
                        )}
                    </Card>

                    {/* Snackbar */}
                    <AppSnackbar
                        openSnackbar={openSnackbar}
                        snackbarMessage={snackbarMessage}
                        setOpenSnackbar={setOpenSnackbar}
                    />

                    {/* Edit Dialog */}
                    <Dialog open={openEditModal} onClose={handleCloseEditModal} fullScreen={fullScreen} fullWidth maxWidth="sm">
                        <DialogTitle>
                            <Typography color="secondary" variant="h5">
                                {t("update_reservation")}
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <Box sx={{display: "flex", justifyContent: "space-between", gap: 2, mt: 1}}>
                                <Box sx={{flex: 1}}>
                                    <Typography variant="subtitle1">{t("check_in")}</Typography>
                                    <Select
                                        fullWidth
                                        value={reservations.find((v) => v.uuid === currentUuid)?.checkIn || ""}
                                        displayEmpty
                                        inputProps={{"aria-label": "checkIn"}}
                                        sx={{mt: 1}}
                                        MenuProps={{PaperProps: {style: {maxHeight: 240}}}}
                                        onChange={(event) => handleSelectChange(event, "checkIn")}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {Array.from({length: 38}, (_, i) => {
                                            const hour = 5 + Math.floor(i / 2);
                                            const minute = i % 2 === 0 ? "00" : "30";
                                            const seconds = "00";
                                            const option = `${hour.toString().padStart(2, "0")}:${minute}:${seconds}`;
                                            return (
                                                <MenuItem key={`in-${option}`} value={option}>
                                                    {option}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </Box>
                                <Box sx={{flex: 1}}>
                                    <Typography variant="subtitle1">{t("check_out")}</Typography>
                                    <Select
                                        fullWidth
                                        value={reservations.find((v) => v.uuid === currentUuid)?.checkOut || ""}
                                        displayEmpty
                                        inputProps={{"aria-label": "checkOut"}}
                                        sx={{mt: 1}}
                                        MenuProps={{PaperProps: {style: {maxHeight: 240}}}}
                                        onChange={(event) => handleSelectChange(event, "checkOut")}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {Array.from({length: 38}, (_, i) => {
                                            const hour = 5 + Math.floor(i / 2);
                                            const minute = i % 2 === 0 ? "00" : "30";
                                            const seconds = "00";
                                            const option = `${hour.toString().padStart(2, "0")}:${minute}:${seconds}`;
                                            return (
                                                <MenuItem key={`out-${option}`} value={option}>
                                                    {option}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </Box>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseEditModal} data-testid="cancel-update-hours-button">{t("cancel")}</Button>
                            <Button onClick={handleUpdateHours} data-testid="save-update-hours-button">{t("save")}</Button>
                        </DialogActions>
                    </Dialog>

                    {/* Confirm Alert */}
                    <AlertDialogSlide
                        title={alertTitle}
                        message={alertMessage}
                        openAlert={openAlert}
                        action={alertAction}
                        onClose={handleCloseAlert}
                    />
                    </Grid>
                </Grid>
            )}
        </>
    );
};

export default withAuthenticationRequired(NewReservationsOverview, {
    onRedirecting: () => <Loading/>,
    returnTo: "/beheer/login",
});
